import { computed, inject, Injectable, signal } from '@angular/core';
import { injectMutation, injectQuery, injectQueryClient } from '@tanstack/angular-query-experimental';
import { lastValueFrom } from 'rxjs';
import { AddressesApiService, Address } from './addresses-api.service';
import { Auth } from 'src/providers/auth/auth';

export const addressKeys = {
  all: ['addresses'] as const,
  lists: () => [...addressKeys.all, 'list'] as const,
  list: (filters?: string) => [...addressKeys.lists(), filters ?? ''] as const,
  details: () => [...addressKeys.all, 'detail'] as const,
  detail: (id: number) => [...addressKeys.details(), id] as const,
};

@Injectable({ providedIn: 'root' })
export class AddressesQueriesService {
  private readonly api = inject(AddressesApiService);
  private readonly auth = inject(Auth);
  private readonly queryClient = injectQueryClient();

  readonly selectedAddressId = signal<number | null>(null);

  // List of all addresses with 3-minute cache (matches current implementation)
  readonly addressesQuery = injectQuery(() => ({
    queryKey: addressKeys.list(),
    queryFn: () => lastValueFrom(this.api.getAddresses()),
    staleTime: 3 * 60 * 1000, // 3 minutes - matches current cache
    gcTime: 10 * 60 * 1000,   // 10 minutes in memory
    enabled: this.auth.isAuthenticated(),
  }));

  /** Resolves from the cache when fresh, otherwise fetches once; for pages that need the list as a promise. */
  getAddressesOnce(): Promise<Address[]> {
    return this.queryClient.ensureQueryData({
      queryKey: addressKeys.list(),
      queryFn: () => lastValueFrom(this.api.getAddresses()),
      staleTime: 3 * 60 * 1000,
    });
  }

  // Computed signals for easy access
  readonly addresses = computed(() => this.addressesQuery.data() ?? []);
  readonly isLoadingAddresses = computed(() => this.addressesQuery.isPending());
  readonly addressesError = computed(() => this.addressesQuery.error());
  readonly hasAtLeastOneAddress = computed(() => this.addresses().length >= 1);
  readonly hasMoreThanOneAddress = computed(() => this.addresses().length > 1);

  // Single address detail query
  readonly selectedAddressQuery = injectQuery(() => ({
    queryKey: addressKeys.detail(this.selectedAddressId() ?? 0),
    queryFn: () => lastValueFrom(this.api.getAddress(this.selectedAddressId()!)),
    enabled: this.selectedAddressId() !== null,
  }));

  readonly selectedAddress = computed(() => this.selectedAddressQuery.data());

  // Mutations
  readonly createAddressMutation = injectMutation(() => ({
    mutationFn: (data: Partial<Address>) => lastValueFrom(this.api.createAddress(data)),
    onSuccess: () => {
      this.queryClient.invalidateQueries({ queryKey: addressKeys.lists() });
    },
  }));

  readonly updateAddressMutation = injectMutation(() => ({
    mutationFn: ({ id, data }: { id: number; data: Partial<Address> }) =>
      lastValueFrom(this.api.updateAddress(id, data)),
    onSuccess: (_, variables) => {
      this.queryClient.invalidateQueries({ queryKey: addressKeys.lists() });
      this.queryClient.invalidateQueries({ queryKey: addressKeys.detail(variables.id) });
    },
  }));

  readonly deleteAddressMutation = injectMutation(() => ({
    mutationFn: (id: number) => lastValueFrom(this.api.deleteAddress(id)),
    onSuccess: () => {
      this.queryClient.invalidateQueries({ queryKey: addressKeys.lists() });
    },
  }));

  // Helper methods
  selectAddress(addressId: number | null): void {
    this.selectedAddressId.set(addressId);
  }

  invalidateAddresses(): void {
    this.queryClient.invalidateQueries({ queryKey: addressKeys.lists() });
  }

  getAddressById(id: number): Address | undefined {
    return this.addresses().find(addr => addr.id === id);
  }

  // Force refresh - for direct integration scenarios where we need fresh data
  async refetch(): Promise<void> {
    await this.addressesQuery.refetch();
  }

  // Promise-based method for compatibility with existing code
  async getAddresses(): Promise<Address[]> {
    // If data is already cached and fresh, return it immediately
    const currentData = this.addressesQuery.data();
    if (currentData) {
      return currentData;
    }

    // Otherwise, refetch and wait for the result
    // TanStack Query's refetch() returns a promise and handles deduplication
    const result = await this.addressesQuery.refetch();
    return result.data ?? [];
  }
}
